import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { TextField, TextArea, SelectField } from "./fields";
import { submitApplication } from "../../lib/api";
import { WORK_AUTH_OPTIONS } from "../../lib/workAuth";

const initial = {
  name: "", email: "", phone: "", linkedin: "", role: "",
  primary_skill: "", years_experience: "", work_authorization: "", message: "",
};

export default function ResumeForm({ initialRole = "" }) {
  const [form, setForm] = useState({ ...initial, role: initialRole });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (initialRole) setForm((f) => ({ ...f, role: initialRole }));
  }, [initialRole]);

  const onFile = (e) => setFile(e.target.files?.[0] || null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("resume", file);
      await submitApplication(fd);
      setSubmitted({ name: form.name, email: form.email, role: form.role });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const applyAgain = () => {
    setForm({ ...initial, role: initialRole });
    setFile(null);
    setSubmitted(null);
  };

  if (submitted) {
    return (
      <div className="text-center" data-testid="resume-success">
        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/10 text-emerald-300">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="font-heading text-2xl font-bold tracking-tight text-white">
          {submitted.name ? `Thanks, ${submitted.name.split(" ")[0]}!` : "Application received"}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          Your application{submitted.role ? <> for <span className="text-white">{submitted.role}</span></> : null} has been received. Our recruiting team will review your profile and reach out with next steps.
        </p>
        <Link
          to={`/portal/login${submitted.email ? `?email=${encodeURIComponent(submitted.email)}` : ""}`}
          className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-400 px-6 py-3.5 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300 sm:w-auto sm:px-10"
          data-testid="resume-track-application"
        >
          Track your application
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
        <p className="mt-4 text-xs text-slate-500">
          We&apos;ll email a secure, passwordless sign-in link to <span className="text-slate-300">{submitted.email || "your inbox"}</span>.
        </p>
        <button
          onClick={applyAgain}
          className="mt-5 text-sm font-medium text-sky-400 hover:text-sky-300"
          data-testid="resume-apply-again"
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="resume-form">
      <TextField label="Position Applying For" name="role" placeholder="e.g. Senior Data Engineer (or General Application)" value={form.role} onChange={set} testId="resume-role" />
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Name" name="name" required value={form.name} onChange={set} testId="resume-name" />
        <TextField label="Email" name="email" type="email" required value={form.email} onChange={set} testId="resume-email" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Phone" name="phone" value={form.phone} onChange={set} testId="resume-phone" />
        <TextField label="LinkedIn" name="linkedin" placeholder="linkedin.com/in/..." value={form.linkedin} onChange={set} testId="resume-linkedin" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label="Primary Skill" name="primary_skill" placeholder="e.g. Data Engineering" value={form.primary_skill} onChange={set} testId="resume-skill" />
        <TextField label="Years of Experience" name="years_experience" placeholder="e.g. 6" value={form.years_experience} onChange={set} testId="resume-experience" />
      </div>
      <SelectField label="Work Authorization / Visa" name="work_authorization" value={form.work_authorization} onChange={set} options={WORK_AUTH_OPTIONS} testId="resume-work-auth" />
      <div>
        <label className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">
          Resume Upload
        </label>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={onFile}
          className="w-full rounded-xl border border-dashed border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-slate-300 file:mr-4 file:rounded-full file:border-0 file:bg-sky-400 file:px-4 file:py-2 file:text-xs file:font-medium file:text-ink-900 hover:file:bg-sky-300"
          data-testid="resume-file"
        />
      </div>
      <TextArea label="Message" name="message" rows={4} value={form.message} onChange={set} testId="resume-message" />
      <button
        type="submit"
        disabled={loading}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-400 px-6 py-4 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300 disabled:opacity-60 sm:w-auto sm:px-10"
        data-testid="resume-submit"
      >
        {loading ? "Submitting..." : "Submit Your Resume"}
      </button>
    </form>
  );
}
